import { Box } from '@eatclubapp/ec-component-library';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { API, Hub } from 'aws-amplify';
import {
  Authenticator,
  Greetings,
  SignUp,
  ForgotPassword,
  SignIn,
  VerifyContact,
} from 'aws-amplify-react';
import { useLocation } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { restaurantBySlug } from '../../data/graphql/queries';
import { devLog } from '../../utils';
import App from '../../App';
import { fetchObeeSsoCredsAction, fetchSsoCredsAction } from '../../data/actions/loginAction';
import { getAppVersion, isEmpty, revertObscureString } from '../../utils/helpers';
import obeeConfig from '../../obee-aws-exports';
import LogMessagesWrapper from '../LogMessages/LogMessagesWrapper';
import CustomSignIn from './CustomSignIn';
import { restaurantActivePropTypes } from '../../data/models/Restaurant';
import PageSkeleton from '../Loading/PageSkeleton';

const AppWithAuth = ({ ssoCreds, fetchSsoCreds, fetchObeeSsoCreds, user, restaurantActive }) => {
  const location = useLocation();
  const obeeLogin = location?.pathname === '/obeeLogin';

  const params = new URLSearchParams(window.location.search);
  const ssoToken = params.get('ssoToken');
  const redirectUrl = params.get('redirectUrl') || '/';

  // Way to switch venues if authorised to
  const venueToSwitchTo = params.get('switchToVenue');

  // const [authSignedIn, setAuthSignedIn] = useState(!!localStorage.getItem('accessToken'));
  const [authSignedIn, setAuthSignedIn] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // After signing in, switch to another venue if doing that
  const switchVenue = async () => {
    if (authSignedIn && venueToSwitchTo) {
      params.delete('switchToVenue');

      try {
        const response = await API.graphql(
          {
            query: restaurantBySlug,
            variables: {
              slug: venueToSwitchTo,
            },
            authMode: 'AMAZON_COGNITO_USER_POOLS',
          },
          {
            'app-version': getAppVersion(),
            'X-App-Environment': import.meta.env?.VITE_API_ENVIRONMENT,
          },
        );

        const restaurantId = response?.data?.restaurantBySlug?.objectId;
        if (restaurantId) {
          devLog('info', 'setting selected restaurant', restaurantId);
          sessionStorage.setItem('selectedRestaurant', restaurantId);
          localStorage.setItem(`lastUsedRestaurant`, restaurantId);
          sessionStorage.removeItem('sessionFilter');
        }

        params.delete('switchToVenue');

        const paramsString = params.toString();

        setAuthSignedIn(true);
        setAuthLoading(false);

        window.location.href = `${redirectUrl}${paramsString ? `?${paramsString}` : ''}`;
      } catch (error) {
        devLog('error', 'restaurants', error);
      }
    }
  };

  useEffect(() => {
    switchVenue();
  }, [authSignedIn, venueToSwitchTo]);

  // TODO also support redirecting to obee login if obee session
  const signOut = () => {
    // params.delete('ssoToken');

    window.location.href = '/';

    // setAuthSignedIn(false);
    // setAuthLoading(true);

    // window.location.href = `${import.meta.env.VITE_EXTERNAL_LOGIN_URL}redirect_url=${
    //   window.location.origin
    // }/login${params.get('restId') ? `&restId=${params.get('restId')}` : ''}`;
  };

  useEffect(() => {
    // temp code to handle obee sso similar to eatclub staff sso
    if (obeeLogin && ssoToken) {
      // Remove whatever was in the session
      devLog('debug', 'Got sso token, removing session storage');
      sessionStorage.removeItem('selectedRestaurant');
      sessionStorage.removeItem('userId');

      // console.log('fetching obee sso creds', ssoToken);
      fetchObeeSsoCreds(ssoToken);
    }

    Hub.listen('auth', ({ payload: { event } }) => {
      switch (event) {
        case 'signIn':
          setAuthSignedIn(true);
          break;
        case 'signOut':
          signOut();
          setAuthSignedIn(false);
          setAuthLoading(false);
          break;
        case 'customOAuthState':
          break;
        case 'signIn_failure':
          break;
        default:
          break;
      }
    });
  }, [fetchSsoCreds, fetchObeeSsoCreds, ssoToken]);

  // Obee login
  useLayoutEffect(() => {
    if (ssoCreds?.success && obeeLogin) {
      const creds = revertObscureString(ssoCreds?.data?.ssoCreds).split(';');

      // TODO this was a quick hack to support obee login
      let keyPrefix = `CognitoIdentityServiceProvider.${
        import.meta.env.VITE_AWS_OBEE_USER_POOLS_WEB_CLIENT_ID
      }`;
      const cognitoLastAuthUser = creds[1];

      localStorage.setItem(`${keyPrefix}.LastAuthUser`, cognitoLastAuthUser);

      keyPrefix = `${keyPrefix}.${cognitoLastAuthUser}`;

      sessionStorage.setItem(`ssoToken`, ssoToken);
      localStorage.setItem(`accessToken`, creds[0]);
      localStorage.setItem(`${keyPrefix}.accessToken`, creds[0]);
      localStorage.setItem(`${keyPrefix}.idToken`, creds[2]);
      localStorage.setItem(`${keyPrefix}.refreshToken`, creds[3]);

      sessionStorage.setItem('userId', creds[1]); // Used for linked venues
      sessionStorage.setItem('selectedRestaurant', creds[1]); // The venue we have "switched to". Elsewhere this can be different to the user ID
      localStorage.setItem(`lastUsedRestaurant`, creds[1]); // Keep track of this venue to prefill the login screen

      params.delete('ssoToken');
      params.delete('redirectUrl');
      const paramsString = params.toString();

      setAuthSignedIn(true);
      setAuthLoading(false);

      window.location.href = `${redirectUrl}${paramsString ? `?${paramsString}` : ''}`;
    }
  }, [ssoCreds?.success]);

  const mapErrorMessages = (message) => {
    if (message === 'Custom auth lambda trigger is not configured for the user pool.') {
      return 'Please enter your password';
    }
    return message;
  };

  // Needed to stop aws injecting "Loading..."
  const signUpConfig = {
    header: 'Sign Up to Obee',
    hiddenDefaults: ['phone_number'],
  };

  const customTheme = {
    button: {
      backgroundColor: '#00AA81',
      fontFamily: 'Inter',
      width: '100%',
      borderRadius: '4px',
    },
    a: {
      color: '#00AA81',
    },
    navButton: {
      backgroundColor: '#00AA81',
      fontFamily: 'Inter',
    },
    sectionFooter: {
      display: 'block',
    },
    sectionFooterPrimaryContent: {
      display: 'block',
      marginBottom: '12px',
    },
    toast: {
      backgroundColor: '#00AA81',
    },
  };

  // console.log('rendering app with auth', {
  //   ssoToken,
  //   authSignedIn,
  //   submitting,
  //   authLoading,
  //   obeeLogin,
  //   obeeSession,
  // });

  const slug = restaurantActive?.restaurant?.slug;

  const redirect = () => {
    if (!ssoToken && !user?.redirectUrl) {
      window.location.href = slug
        ? `${import.meta.env.VITE_EXTERNAL_LOGOUT_URL_START}${slug}${import.meta.env.VITE_EXTERNAL_LOGOUT_URL_END}`
        : '/obeeLogin';
      // window.location.href = '/obeeLogin';
      // window.location.href = `${import.meta.env.VITE_LOGIN_URL}`;
    }

    // if (!ssoToken) {
    //   window.location.href = `${import.meta.env.VITE_LOGIN_URL}?callback_url=${
    //     window.location.origin
    //   }/login${params.get('restId') ? `&restId=${params.get('restId')}` : ''}`;
    // }
  };

  const redirectToHome = () => {
    window.location.href = '/';
  };

  if (user?.loggingOut) {
    return (
      <Box
        style={{
          margin: '25vh auto 0',
          transform: 'translateY(-50%)',
          width: 'fit-content',
        }}
      >
        Logging out...
      </Box>
    );
  }

  if (authSignedIn && venueToSwitchTo) {
    return <Box>Switching venue</Box>;
  }

  return (
    <Authenticator
      onStateChange={(authState) => {
        if (authState === 'signedIn') {
          // NOTE: this will trigger when refreshing and logged in
          // show app here to prevent showing both login screen, and app
          setAuthSignedIn(true);
          setAuthLoading(false);
          return;
        }
        if (authState === 'signIn') {
          setAuthLoading(false);
        }
      }}
      hide={[Greetings, SignIn, SignUp, ForgotPassword, VerifyContact]}
      hideDefault
      includeGreetings={false}
      amplifyConfig={obeeConfig}
      theme={customTheme}
      signUpConfig={signUpConfig}
      usernameAttributes='email'
      errorMessage={mapErrorMessages}
    >
      <>
        {ssoCreds?.error && signOut()}
        {/* {!authLoading && !authSignedIn && isEmpty(ssoToken?.data) && redirect()} */}
        {!authLoading && authSignedIn && obeeLogin && isEmpty(ssoToken) && redirectToHome()}
        {!authLoading && !authSignedIn && !obeeLogin && isEmpty(ssoToken) && redirect()}
        {!authLoading && !authSignedIn && obeeLogin && isEmpty(ssoToken) && (
          <CustomSignIn authSignedIn={authSignedIn} />
        )}
        {authLoading && !authSignedIn && <PageSkeleton />}
        {authSignedIn && <App />}
        {!authLoading && !authSignedIn && obeeLogin && !isEmpty(ssoToken) && (
          <PageSkeleton />
          // <Box
          //   style={{
          //     margin: '25vh auto 0',
          //     transform: 'translateY(-50%)',
          //     width: 'fit-content',
          //   }}
          // >
          //   Logging in. This may take a few seconds.
          // </Box>
        )}
        <LogMessagesWrapper />
      </>
    </Authenticator>
  );
};

AppWithAuth.propTypes = {
  fetchSsoCreds: PropTypes.func.isRequired,
  fetchObeeSsoCreds: PropTypes.func.isRequired,
  ssoCreds: PropTypes.shape({
    data: PropTypes.shape({
      ssoCreds: PropTypes.string,
    }),
    error: PropTypes.string,
    pending: PropTypes.bool,
    success: PropTypes.bool,
  }).isRequired,
  user: PropTypes.shape({
    redirectUrl: PropTypes.string,
    loggingOut: PropTypes.bool,
  }).isRequired,
  restaurantActive: restaurantActivePropTypes.isRequired,
};

const mapStateToProps = (state) => ({
  ssoCreds: state.ssoCreds,
  user: state.user,
  restaurantActive: state.restaurantActive,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchSsoCreds: fetchSsoCredsAction,
      fetchObeeSsoCreds: fetchObeeSsoCredsAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(AppWithAuth);
